
import { supabase } from "@/lib/supabase";
import { Insight } from "@/utils/insights-utils";
import { Industry, OrganizationSize, AssessmentDimension } from "@/types/assessment";

interface AssessmentDataForInsights {
  organizationDetails: {
    industry: Industry;
    size: OrganizationSize;
  };
  dimensionScores: Record<string, number>;
  derivedMetrics?: {
    averageScore: number;
    lowestScore: number;
    highestScore: number;
    lowestDimension: AssessmentDimension;
    highestDimension: AssessmentDimension;
    scoreDifference: number;
    overallMaturityLevel: string;
  };
}

export async function fetchExistingInsights(assessmentId: string): Promise<Insight[] | null> {
  try {
    const { data, error } = await supabase
      .from('assessment_insights')
      .select('*')
      .eq('assessment_id', assessmentId);

    if (error) throw error;

    if (data && data.length > 0) {
      const uniqueInsights = Object.values(
        data.reduce((acc, insight) => ({
          ...acc,
          [insight.category]: {
            category: insight.category,
            insight: insight.insight,
            confidence_score: insight.confidence_score,
            metadata: insight.metadata as Record<string, any> || {}
          }
        }), {})
      ) as Insight[];

      return uniqueInsights;
    }
    
    return null;
  } catch (error) {
    console.error("Error fetching insights:", error);
    throw error;
  }
}

export async function checkAssessmentExists(assessmentId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('assessments')
      .select('id')
      .eq('id', assessmentId)
      .maybeSingle();
      
    if (error) {
      console.error("Error checking assessment:", error);
      throw new Error("Failed to verify assessment record.");
    }
    
    return !!data;
  } catch (error) {
    console.error("Error checking if assessment exists:", error);
    throw error;
  }
}

export async function generateLLMInsights(
  assessmentId: string, 
  assessmentData: AssessmentDataForInsights
): Promise<Insight[]> {
  try {
    // Calculate additional metrics for better LLM context if not already provided
    let enhancedAssessmentData = { ...assessmentData };
    
    if (!enhancedAssessmentData.derivedMetrics) {
      const scores = assessmentData.dimensionScores;
      const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / 
                       Object.values(scores).length;
      const lowestScore = Math.min(...Object.values(scores));
      const highestScore = Math.max(...Object.values(scores));
      const lowestDimension = Object.entries(scores)
                             .find(([_, score]) => score === lowestScore)?.[0] as AssessmentDimension;
      const highestDimension = Object.entries(scores)
                              .find(([_, score]) => score === highestScore)?.[0] as AssessmentDimension;
      
      enhancedAssessmentData.derivedMetrics = {
        averageScore: avgScore,
        lowestScore,
        highestScore,
        lowestDimension,
        highestDimension,
        scoreDifference: highestScore - lowestScore,
        overallMaturityLevel: avgScore < 2 ? 'Beginner' : 
                             avgScore < 3 ? 'Developing' : 
                             avgScore < 4 ? 'Established' : 
                             avgScore < 4.5 ? 'Advanced' : 'Leading'
      };
    }

    const { data, error } = await supabase.functions.invoke(
      'analyze-assessment',
      {
        body: { 
          assessmentId, 
          assessmentData: enhancedAssessmentData,
          // Pass detailed contextual information for better LLM understanding
          context: {
            dimensionDescriptions: {
              strategy: "Focus on AI vision, roadmap, and business model integration",
              data: "Quality, availability, and governance of data assets",
              talent: "AI skills, roles, and organizational structure",
              deployment: "Development practices, infrastructure, and operationalization",
              innovation: "Experimentation, continuous improvement, and adoption of emerging AI technologies"
            },
            maturityLevelDescriptions: {
              "Beginner": "Initial exploration of AI capabilities",
              "Developing": "Building foundational AI capabilities",
              "Established": "Systematic AI implementation across multiple areas",
              "Advanced": "Integrated AI capabilities driving business value",
              "Leading": "AI as a competitive advantage and core to the business model"
            },
            industryTrends: {
              industry: assessmentData.organizationDetails.industry,
              size: assessmentData.organizationDetails.size
            }
          }
        }
      }
    );

    if (error) throw error;
    if (!data?.insights) throw new Error('No insights returned from analysis');
    
    return data.insights;
  } catch (error) {
    console.error("Error generating LLM insights:", error);
    throw error;
  }
}
